import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import ClusterDenLogo from "../../Images/Login/ClusterDenLogo.png";
import ClusterDenLogoWhite from "../../Images/Login/ClusterDenLogoWhite.png";
import SignupFeaturesHero from "../../Images/Login/signup-features-hero.png";
import EmailVerifyEnvelope from "../../Images/Login/email-verify-envelope.svg";
import EmailVerifyCheckBadge from "../../Images/Login/email-verify-check-badge.svg";
import SabrosoLogo from "../../Images/Login/TrustedBy/sabroso.png";
import ChoudharyTravelsLogo from "../../Images/Login/TrustedBy/choudhary-travels.png";
import TradeLinksLogo from "../../Images/Login/TrustedBy/trade-links.png";
import WhitesCastleLogo from "../../Images/Login/TrustedBy/whites-castle.png";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useThemeContext } from "../../contexts/ThemeContext";
import { TilesBackground } from "../../components/Auth/TilesBackground";

const RESEND_COOLDOWN_SECONDS = 30;
const MARQUEE_COMPANIES = [
  { name: "Sabroso", logo: SabrosoLogo },
  { name: "Choudhary Group of Travels", logo: ChoudharyTravelsLogo },
  { name: "Trade Links", logo: TradeLinksLogo },
  // Taller: its wordmark text is small relative to the "W" mark, needs more height to stay legible.
  { name: "White's Castle", logo: WhitesCastleLogo, tall: true },
];

// eslint-disable-next-line no-unused-vars
function GoogleLogo({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12
        c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24
        c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
      />
      <path
        fill="#FF3D00"
        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039
        l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36
        c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
      />
      <path
        fill="#1976D2"
        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571
        c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
      />
    </svg>
  );
}

function validatePasswordClient(pwd) {
  const unmet = [];
  if (pwd.length < 8) unmet.push("at least 8 characters");
  if (!/[a-z]/.test(pwd)) unmet.push("one lowercase letter");
  if (!/[A-Z]/.test(pwd)) unmet.push("one uppercase letter");
  if (!/[0-9]/.test(pwd)) unmet.push("one digit");
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) {
    unmet.push("one special character");
  }
  return unmet;
}

// Same responsive fixes as Login.jsx (see AUTH_COMPACT_HEIGHT_MQ there for the full
// rationale): shrink the branding panel's fixed chrome on short viewports so the
// illustration doesn't get squeezed into a tiny image surrounded by dead space.
const AUTH_COMPACT_HEIGHT_MQ = "@media (max-height:800px)";

const Signup = () => {
  const [profile, setProfile] = useState({ email: "", password: "" });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [signupComplete, setSignupComplete] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState("");
  const [resending, setResending] = useState(false);
  const cooldownIntervalRef = useRef(null);

  const { mode } = useThemeContext();
  const navigate = useNavigate();
  // Carried through from installEntry/the OAuth callback when this signup
  // was reached via the Shopify install flow for a shop ClusterDen didn't
  // recognize yet — forwarded to the backend so it can claim the pending
  // Shopify connection once this signup creates a real workspace (see
  // finalizePendingShopifyConnection in shopifyOAuthService.js).
  const [searchParams] = useSearchParams();
  const shop = searchParams.get("shop") || "";

  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    };
  }, []);

  const startResendCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
    cooldownIntervalRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!profile.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      newErrors.email = "Enter a valid email address";
    }
    const unmet = validatePasswordClient(profile.password || "");
    if (unmet.length) newErrors.password = `Password must contain ${unmet.join(", ")}.`;
    if (!agreedToTerms) {
      newErrors.terms = "Please agree to the Terms of Service and Privacy Policy to continue";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitSignup = async () => {
    if (!validateForm()) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      const response = await fetch(`${apiUrl}/api/accounts/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ownerGmail: profile.email.trim(),
          ownerPassword: profile.password,
          plan: "trial",
          ...(shop && { shop }),
        }),
      });
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Server error: ${response.status} ${response.statusText}. ${text.substring(0, 120)}`);
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || "Signup failed");
      setSubmitting(false);
      setSignupComplete(true);
      startResendCooldown();
    } catch (err) {
      setSubmitError(err.message || "Failed to create your account.");
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setResendMessage("");
    try {
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      const response = await fetch(`${apiUrl}/api/accounts/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: profile.email.trim() }),
      });
      const data = await response.json();
      setResendMessage(data.message || (data.success ? "Verification email sent." : "Unable to resend right now."));
      if (data.success || response.status === 429) {
        startResendCooldown();
      }
    } catch (err) {
      setResendMessage("Unable to resend right now. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const getFieldStyles = () => {
    if (mode === "dark") {
      return {
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#141414",
          borderRadius: "12px",
          transition: "all 0.3s ease",
          "& fieldset": { borderColor: "#333333", borderWidth: "1.5px" },
          "&:hover": {
            backgroundColor: "#1c1c1c",
            "& fieldset": { borderColor: "#4a4a4a" },
          },
          "&.Mui-focused": {
            backgroundColor: "#1c1c1c",
            "& fieldset": { borderColor: "#34d399", borderWidth: "2px" },
          },
        },
        "& .MuiInputLabel-root": {
          color: "#c2c7d0",
          fontFamily: "Instrument Sans, sans-serif",
          fontSize: "1.05rem",
          fontWeight: 500,
          "&.MuiInputLabel-shrink": { fontSize: "1rem" },
          "&.Mui-focused": { color: "#34d399", fontWeight: 600 },
        },
        "& .MuiInputBase-input": {
          color: "#f5f5f5",
          fontFamily: "Instrument Sans, sans-serif",
          fontSize: "14.5px",
          padding: "11px 14px",
          "&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #141414 inset !important",
            WebkitTextFillColor: "#f5f5f5 !important",
            caretColor: "#f5f5f5",
            transition: "background-color 5000s ease-in-out 0s",
          },
          "&:-webkit-autofill:hover": { WebkitBoxShadow: "0 0 0 100px #1c1c1c inset !important" },
          "&:-webkit-autofill:focus": { WebkitBoxShadow: "0 0 0 100px #1c1c1c inset !important" },
          "&::selection": { backgroundColor: "rgba(79,195,137,0.4)", color: "#ffffff" },
        },
        "& .MuiSvgIcon-root": { color: "#6b7280" },
        "& .MuiFormHelperText-root": {
          color: "#9ca3af",
          "&.Mui-error": { color: "#ef4444" },
        },
      };
    }
    return {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#F3F4F6",
        borderRadius: "12px",
        "& fieldset": { borderColor: "#E5E7EB" },
        "&:hover fieldset": { borderColor: "#D1D5DB" },
        "&.Mui-focused fieldset": { borderColor: "#a0eaa3", borderWidth: "2px" },
      },
      "& .MuiInputLabel-root": {
        color: "#4B5563",
        fontFamily: "Instrument Sans, sans-serif",
        fontSize: "1.05rem",
        fontWeight: 500,
        "&.MuiInputLabel-shrink": { fontSize: "1rem" },
        "&.Mui-focused": { color: "#2D9B39", fontWeight: 600 },
      },
      "& .MuiInputBase-input": {
        color: "#111827",
        fontFamily: "Instrument Sans, sans-serif",
        fontSize: "17px",
        padding: "16px 16px",
        "&:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important",
          WebkitTextFillColor: "#111827 !important",
          caretColor: "#111827",
          transition: "background-color 5000s ease-in-out 0s",
        },
        "&:-webkit-autofill:hover": { WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important" },
        "&:-webkit-autofill:focus": { WebkitBoxShadow: "0 0 0 100px #F3F4F6 inset !important" },
        "&::selection": { backgroundColor: "rgba(68,181,124,0.3)", color: "#111827" },
      },
    };
  };

  const accentColor = mode === "dark" ? "#4FC389" : "#44B57C";

  const fieldLabelSx = {
    mb: 0.75,
    fontWeight: 600,
    fontSize: "15px",
    color: mode === "dark" ? "#d1d5db" : "#374151",
    fontFamily: "Instrument Sans, sans-serif",
  };
  const stepHeadingSx = {
    mb: 0,
    lineHeight: 1.2,
    fontWeight: 700,
    fontSize: { xs: "1.6rem", sm: "1.75rem" },
    color: mode === "dark" ? "#f5f5f5" : "#121a34",
    fontFamily: "Instrument Sans, sans-serif",
    textAlign: "center",
    [AUTH_COMPACT_HEIGHT_MQ]: { fontSize: "1.2rem" },
  };
  const stepSubheadingSx = {
    mt: 0.5,
    mb: 2.25,
    fontSize: "15px",
    color: mode === "dark" ? "#9ca3af" : "#6b7280",
    fontFamily: "Instrument Sans, sans-serif",
    textAlign: "center",
    [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1.25 },
  };

  // Full-screen loading overlay while account is being created
  if (submitting) {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: mode === "dark" ? "#121212" : "linear-gradient(180deg, #f3f5fb 0%, #eceff8 100%)",
          gap: 3,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            component="img"
            src={mode === "dark" ? ClusterDenLogoWhite : ClusterDenLogo}
            alt="ClusterDen"
            sx={{ height: 32, width: "auto" }}
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            width: 72,
            height: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress
            size={72}
            thickness={2.5}
            sx={{ color: accentColor, position: "absolute" }}
          />
          <BusinessOutlinedIcon sx={{ fontSize: 28, color: accentColor }} />
        </Box>
        <Typography
          sx={{
            fontFamily: "Instrument Sans, sans-serif",
            fontWeight: 700,
            fontSize: "1.4rem",
            color: mode === "dark" ? "#f5f5f5" : "#121a34",
            textAlign: "center",
          }}
        >
          Creating your account…
        </Typography>
        <Typography
          sx={{
            fontFamily: "Instrument Sans, sans-serif",
            fontSize: "0.93rem",
            color: mode === "dark" ? "#9ca3af" : "#66708f",
            textAlign: "center",
            maxWidth: 340,
            lineHeight: 1.6,
          }}
        >
          This only takes a moment.
        </Typography>
      </Box>
    );
  }

  // Full-page "check your email" state — no left branding card, just the message
  // centered over the same tiled background used on the right side of the split layout.
  if (signupComplete) {
    return (
      <Box
        sx={{
          height: "100dvh",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Instrument Sans, sans-serif",
          backgroundColor: mode === "dark" ? "#121212" : "#FAFBFC",
        }}
      >
        <TilesBackground
          borderColor={mode === "dark" ? "rgba(255,255,255,0.09)" : "rgba(17,24,39,0.05)"}
          hoverColor={mode === "dark" ? "rgba(79,195,137,0.16)" : "rgba(68,181,124,0.12)"}
        />
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, boxSizing: "border-box" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: 460,
              boxSizing: "border-box",
              mx: "auto",
              py: { xs: 3.5, sm: 4.5, md: 5 },
              px: { xs: 3, sm: 4.5, md: 5 },
              borderRadius: 4,
              backgroundColor: mode === "dark" ? "#1E1E1E" : "#FFFFFF",
              border: mode === "dark" ? "2px solid rgba(255,255,255,0.12)" : "none",
              boxShadow:
                mode === "dark"
                  ? "0 2px 10px rgba(0,0,0,0.45), 0 30px 80px rgba(0,0,0,0.7)"
                  : "0 2px 10px rgba(17,24,39,0.08), 0 30px 70px rgba(17,24,39,0.18)",
            }}
          >
            <Box component="form" noValidate>
              <Box
                sx={{
                  position: "relative",
                  width: 108,
                  height: 108,
                  mx: "auto",
                  mb: 3,
                }}
              >
                <Box
                  component="img"
                  src={EmailVerifyEnvelope}
                  alt=""
                  sx={{ width: "100%", height: "100%", display: "block" }}
                />
                <Box
                  component="img"
                  src={EmailVerifyCheckBadge}
                  alt=""
                  sx={{ position: "absolute", right: -6, bottom: -6, width: "38%", height: "38%" }}
                />
              </Box>

              <Typography variant="h6" sx={stepHeadingSx}>
                Check your email
              </Typography>
              <Typography sx={stepSubheadingSx}>
                We sent a verification link to <strong>{profile.email}</strong>.
                <br />
                Click it to activate your account and continue setting up your workspace.
              </Typography>

              {resendMessage && (
                <Typography
                  sx={{
                    mb: 2,
                    fontSize: "13px",
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    fontFamily: "Instrument Sans, sans-serif",
                    textAlign: "center",
                  }}
                >
                  {resendMessage}
                </Typography>
              )}

              <Button
                onClick={handleResend}
                disabled={resendCooldown > 0 || resending}
                fullWidth
                variant="outlined"
                sx={{
                  py: 2,
                  borderColor: mode === "dark" ? "#333333" : "#E1E4E8",
                  color: mode === "dark" ? "#f5f5f5" : "#3C4043",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  fontFamily: "Instrument Sans, sans-serif",
                  "&:hover": {
                    borderColor: mode === "dark" ? "#4a4a4a" : "#C1C8CD",
                    backgroundColor: mode === "dark" ? "#1c1c1c" : "#F8F9FA",
                  },
                }}
              >
                {resending
                  ? "Sending…"
                  : resendCooldown > 0
                  ? `Resend email (${resendCooldown}s)`
                  : "Resend email"}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100dvh",
        overflow: "hidden",
        background: mode === "dark" ? "#121212" : "#FAFBFC",
        fontFamily: "Instrument Sans, sans-serif",
        transition: "background-color 0.3s ease",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        // Caps the two-panel layout on genuinely ultrawide monitors only — see Login.jsx's
        // identical rule for why this uses an explicit min-width instead of MUI's "xl"
        // breakpoint (1536px is too low; 1920px is a completely standard monitor width).
        maxWidth: "none",
        "@media (min-width:2200px)": { maxWidth: "1800px" },
        mx: "auto",
      }}
    >
        {/* Left branding card — standalone, floats with margin, not attached to the form */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flex: "0 0 39%",
            flexDirection: "column",
            m: { md: 3 },
            p: { md: 5 },
            pl: { md: 5, lg: 5.5 },
            borderRadius: 5,
            overflow: "hidden",
            backgroundColor: mode === "dark" ? "#1E1E1E" : "#FAFBFC",
            border: mode === "dark" ? "none" : "2px solid rgba(0,0,0,0.1)",
            color: mode === "dark" ? "#f5f5f5" : "#000000",
            position: "relative",
            [AUTH_COMPACT_HEIGHT_MQ]: { m: 1.5, p: 3, pl: { md: 4, lg: 4.5 } },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 5,
              [AUTH_COMPACT_HEIGHT_MQ]: { mb: 2 },
            }}
          >
            <Box
              component="img"
              src={mode === "dark" ? ClusterDenLogoWhite : ClusterDenLogo}
              alt="ClusterDen"
              sx={{
                height: 44,
                width: "auto",
                maxWidth: 200,
                objectFit: "contain",
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                onClick={() => window.open("https://wa.me/923250909111", "_blank", "noopener,noreferrer")}
                sx={{
                  p: 0,
                  minWidth: "auto",
                  fontSize: "13px",
                  fontWeight: 500,
                  textTransform: "none",
                  color: mode === "dark" ? "#9ca3af" : "#6B7280",
                  fontFamily: "Instrument Sans, sans-serif",
                  "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
                }}
              >
                Help
              </Button>
              <Typography sx={{ fontSize: "13px", color: mode === "dark" ? "#3f3f3f" : "#D1D5DB" }}>·</Typography>
              <Button
                onClick={() => window.open("https://clusterden.com/privacy", "_blank", "noopener,noreferrer")}
                sx={{
                  p: 0,
                  minWidth: "auto",
                  fontSize: "13px",
                  fontWeight: 500,
                  textTransform: "none",
                  color: mode === "dark" ? "#9ca3af" : "#6B7280",
                  fontFamily: "Instrument Sans, sans-serif",
                  "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
                }}
              >
                Privacy
              </Button>
              <Typography sx={{ fontSize: "13px", color: mode === "dark" ? "#3f3f3f" : "#D1D5DB" }}>·</Typography>
              <Button
                onClick={() => window.open("https://clusterden.com/terms", "_blank", "noopener,noreferrer")}
                sx={{
                  p: 0,
                  minWidth: "auto",
                  fontSize: "13px",
                  fontWeight: 500,
                  textTransform: "none",
                  color: mode === "dark" ? "#9ca3af" : "#6B7280",
                  fontFamily: "Instrument Sans, sans-serif",
                  "&:hover": { color: mode === "dark" ? "#ffffff" : "#000000", backgroundColor: "transparent" },
                }}
              >
                Terms
              </Button>
            </Box>
          </Box>

          {/* Headline */}
          <Typography
            component="h1"
            sx={{
              fontWeight: 500,
              fontSize: { md: "1.95rem", lg: "2.25rem" },
              lineHeight: 1.25,
              letterSpacing: "-0.015em",
              mb: 2,
              whiteSpace: "nowrap",
              color: mode === "dark" ? "#f5f5f5" : "#000000",
            }}
          >
            The simplest way to manage
            <br />
            your workspace
          </Typography>
          <Typography
            sx={{
              fontSize: { md: "0.95rem", lg: "1.05rem" },
              lineHeight: 1.6,
              color: "#9CA3AF",
              whiteSpace: "nowrap",
            }}
          >
            Connect WhatsApp, Instagram, and Facebook in one shared inbox.
          </Typography>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 0,
            }}
          >
            <Box
              component="img"
              src={SignupFeaturesHero}
              alt=""
              sx={{
                maxWidth: "82%",
                maxHeight: "75%",
                [AUTH_COMPACT_HEIGHT_MQ]: { maxWidth: "90%", maxHeight: "92%" },
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Trusted by — auto-scrolling marquee */}
          <Box
            sx={{
              overflow: "hidden",
              width: "100%",
              maskImage: "linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)",
              "@keyframes signupTrustedMarquee": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(-33.3333%)" },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "max-content",
                gap: { md: 2.5, lg: 3 },
                animation: "signupTrustedMarquee 16s linear infinite",
                "&:hover": { animationPlayState: "paused" },
              }}
            >
              {[...MARQUEE_COMPANIES, ...MARQUEE_COMPANIES, ...MARQUEE_COMPANIES].map((company, i) => (
                <Box
                  key={`${company.name}-${i}`}
                  component="img"
                  src={company.logo}
                  alt={company.name}
                  sx={{
                    width: { md: 88, lg: 100 },
                    height: company.tall ? { md: 44, lg: 48 } : { md: 30, lg: 33 },
                    objectFit: "contain",
                    objectPosition: "center center",
                    opacity: 0.75,
                    filter: mode === "dark" ? "invert(1)" : "none",
                    flexShrink: 0,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right side — animated tile background with the form as a floating card on top */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            // "safe center" so growing content (e.g. the password-requirements checklist)
            // never pushes part of the card above the visible/scrollable area — see
            // Login.jsx's identical rule for the full explanation.
            justifyContent: "safe center",
            boxSizing: "border-box",
            padding: { xs: 2.5, sm: 3, md: 4 },
            overflowY: "auto",
            overflowX: "hidden",
            backgroundColor: mode === "dark" ? "#121212" : "#FAFBFC",
          }}
        >
          <TilesBackground
            borderColor={mode === "dark" ? "rgba(255,255,255,0.09)" : "rgba(17,24,39,0.05)"}
            hoverColor={mode === "dark" ? "rgba(79,195,137,0.16)" : "rgba(68,181,124,0.12)"}
          />
          <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, boxSizing: "border-box" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                maxWidth: 520,
                boxSizing: "border-box",
                mx: "auto",
                py: { xs: 3.5, sm: 4.5, md: 5 },
                px: { xs: 3, sm: 4.5, md: 5 },
                [AUTH_COMPACT_HEIGHT_MQ]: { py: 2 },
                borderRadius: 4,
                backgroundColor: mode === "dark" ? "#1E1E1E" : "#FFFFFF",
                border: mode === "dark" ? "2px solid rgba(255,255,255,0.12)" : "none",
                boxShadow:
                  mode === "dark"
                    ? "0 2px 10px rgba(0,0,0,0.45), 0 30px 80px rgba(0,0,0,0.7)"
                    : "0 2px 10px rgba(17,24,39,0.08), 0 30px 70px rgba(17,24,39,0.18)",
              }}
            >
              <>
                  <Box component="form" noValidate>
                    <Typography variant="h6" sx={stepHeadingSx}>
                      Create Your Account
                    </Typography>
                    <Typography sx={stepSubheadingSx}>
                      Enter your email and set a password to get started.
                    </Typography>

                    <Typography sx={fieldLabelSx}>Email Address</Typography>

                    <TextField
                      fullWidth
                      name="email"
                      placeholder="your@email.com"
                      type="email"
                      spellCheck={false}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                      value={profile.email}
                      onChange={handleProfileChange}
                      required
                      error={!!errors.email}
                      helperText={errors.email}
                      sx={{ mb: 1.75, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1 }, ...getFieldStyles() }}
                    />

                    <Typography sx={fieldLabelSx}>Password</Typography>

                    <TextField
                      fullWidth
                      name="password"
                      value={profile.password}
                      onChange={handleProfileChange}
                      required
                      type={showPassword ? "text" : "password"}
                      error={!!errors.password}
                      helperText={errors.password}
                      onFocus={() => setPasswordFocused(true)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{
                                color: mode === "dark" ? "#757575" : "#9CA3AF",
                                "&:hover": { color: mode === "dark" ? "#9E9E9E" : "#6B7280", backgroundColor: "transparent" },
                              }}
                            >
                              {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 1.5, [AUTH_COMPACT_HEIGHT_MQ]: { mb: 0.85 }, ...getFieldStyles() }}
                    />

                    {(() => {
                      const pwChecks = [
                        { key: "len", label: "At least 8 characters", met: profile.password.length >= 8 },
                        { key: "low", label: "One lowercase letter", met: /[a-z]/.test(profile.password) },
                        { key: "up", label: "One uppercase letter", met: /[A-Z]/.test(profile.password) },
                        { key: "dig", label: "One number", met: /[0-9]/.test(profile.password) },
                        { key: "spec", label: "One special character", met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(profile.password) },
                      ];
                      const allMet = pwChecks.every((c) => c.met);
                      if (!passwordFocused || allMet) return null;
                      return (
                        <Box
                          sx={{
                            mb: 1.5,
                            p: "8px 12px",
                            borderRadius: "10px",
                            border: `1px solid ${mode === "dark" ? "#333" : "#e5e7eb"}`,
                            backgroundColor: mode === "dark" ? "#2a2a2a" : "#f9fafb",
                            [AUTH_COMPACT_HEIGHT_MQ]: { mb: 1, p: "5px 10px" },
                          }}
                        >
                          {pwChecks.map((item) => (
                            <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 0.75, py: "2px" }}>
                              {item.met ? (
                                <CheckCircleOutlineIcon sx={{ fontSize: 15, color: mode === "dark" ? "#4ade80" : "#16a34a" }} />
                              ) : (
                                <RadioButtonUncheckedIcon sx={{ fontSize: 15, color: mode === "dark" ? "#555" : "#bbb" }} />
                              )}
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: item.met
                                    ? mode === "dark" ? "#4ade80" : "#16a34a"
                                    : mode === "dark" ? "#9ca3af" : "#6b7280",
                                  fontWeight: item.met ? 500 : 400,
                                  fontFamily: "Instrument Sans, sans-serif",
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                        mb: errors.terms ? 0.5 : 1.75,
                        mt: 0.25,
                        [AUTH_COMPACT_HEIGHT_MQ]: { mb: errors.terms ? 0.5 : 1, mt: 0 },
                      }}
                    >
                      <Checkbox
                        checked={agreedToTerms}
                        onChange={(e) => {
                          setAgreedToTerms(e.target.checked);
                          setErrors((prev) => ({ ...prev, terms: "" }));
                        }}
                        size="small"
                        sx={{
                          p: 0,
                          mt: "1px",
                          color: mode === "dark" ? "#4b5563" : "#9CA3AF",
                          "&.Mui-checked": { color: accentColor },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "13px",
                          lineHeight: 1.5,
                          color: mode === "dark" ? "#d1d5db" : "#374151",
                          fontFamily: "Instrument Sans, sans-serif",
                        }}
                      >
                        By checking this box, I confirm that I have read and agree to ClusterDen's{" "}
                        <Box
                          component="span"
                          onClick={() => window.open("https://clusterden.com/terms", "_blank", "noopener,noreferrer")}
                          sx={{ color: accentColor, fontWeight: 600, cursor: "pointer" }}
                        >
                          Terms of Service
                        </Box>{" "}
                        and{" "}
                        <Box
                          component="span"
                          onClick={() => window.open("https://clusterden.com/privacy", "_blank", "noopener,noreferrer")}
                          sx={{ color: accentColor, fontWeight: 600, cursor: "pointer" }}
                        >
                          Privacy Policy
                        </Box>
                        .
                      </Typography>
                    </Box>
                    {errors.terms && (
                      <Typography
                        sx={{
                          color: mode === "dark" ? "#f87171" : "#EF4444",
                          fontSize: "14px",
                          mb: 2,
                          fontFamily: "Instrument Sans, sans-serif",
                        }}
                      >
                        {errors.terms}
                      </Typography>
                    )}

                    {submitError && (
                      <Typography
                        sx={{
                          color: mode === "dark" ? "#f87171" : "#EF4444",
                          fontSize: "15px",
                          mb: 2,
                          fontFamily: "Instrument Sans, sans-serif",
                        }}
                      >
                        {submitError}
                      </Typography>
                    )}

                    <Button
                      onClick={handleSubmitSignup}
                      fullWidth
                      variant="contained"
                      sx={{
                        py: 1.25,
                        mt: 0.5,
                        [AUTH_COMPACT_HEIGHT_MQ]: { py: 1, mt: 0.25 },
                        backgroundColor: accentColor,
                        color: "#ffffff",
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "1rem",
                        fontFamily: "Instrument Sans, sans-serif",
                        boxShadow:
                          mode === "dark"
                            ? "none"
                            : "0 2px 8px rgba(68,181,124,0.25)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: mode === "dark" ? "#3aad77" : "#38a06c",
                          boxShadow:
                            mode === "dark"
                              ? "none"
                              : "0 8px 20px rgba(68,181,124,0.35)",
                        },
                      }}
                    >
                      Create Account
                    </Button>
                  </Box>

                  <Box sx={{ textAlign: "left", mt: 1.25, [AUTH_COMPACT_HEIGHT_MQ]: { mt: 0.75 } }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Instrument Sans, sans-serif",
                        color: mode === "dark" ? "#A1A1AA" : "#6B7280",
                        fontSize: "14px",
                        display: "inline",
                      }}
                    >
                      Already have a workspace?{" "}
                      <Button
                        onClick={() => navigate(shop ? `/?shop=${encodeURIComponent(shop)}` : "/")}
                        sx={{
                          color: mode === "dark" ? "#10b981" : "#2D9B39",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "14px",
                          p: 0,
                          minWidth: "auto",
                          fontFamily: "Instrument Sans, sans-serif",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: mode === "dark" ? "#059669" : "#1a7f64",
                          },
                        }}
                      >
                        Login
                      </Button>
                    </Typography>
                  </Box>

                  {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, my: 1.75, [AUTH_COMPACT_HEIGHT_MQ]: { my: 1 } }}>
                    <Box sx={{ flex: 1, height: "1px", backgroundColor: mode === "dark" ? "#333333" : "#E5E7EB" }} />
                    <Typography
                      sx={{
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: mode === "dark" ? "#6b7280" : "#9CA3AF",
                        fontFamily: "Instrument Sans, sans-serif",
                      }}
                    >
                      OR
                    </Typography>
                    <Box sx={{ flex: 1, height: "1px", backgroundColor: mode === "dark" ? "#333333" : "#E5E7EB" }} />
                  </Box> */}

{/*                   <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GoogleLogo size={16} />}
                    sx={{
                      py: 1,
                      [AUTH_COMPACT_HEIGHT_MQ]: { py: 0.75 },
                      borderColor: mode === "dark" ? "#333333" : "#E1E4E8",
                      color: mode === "dark" ? "#f5f5f5" : "#111827",
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.92rem",
                      fontFamily: "Instrument Sans, sans-serif",
                      "&:hover": {
                        borderColor: mode === "dark" ? "#4a4a4a" : "#C1C8CD",
                        backgroundColor: mode === "dark" ? "#1c1c1c" : "#F8F9FA",
                      },
                    }}
                  >
                    Continue with Google
                  </Button> */}
                </>
            </Box>
          </Container>
        </Box>
    </Box>
  );
};

export default Signup;
